// Third party
import moment, { Moment } from "moment";

// Types
import { ISalesAnalyticsItem, ISalesLog, ISalesProduct } from "@DataPages/types/salesOperations/salesLog/model";
import { DATE_FORMAT } from "@/Constants/date";

type TUnits = {
	todayUnits: number;
	totalUnits: number;
	totalMonthUnits: number;
};

const calculateProjectedUnits = (units: TUnits, workedDays: number, daysInMonth: number) => {
	let projectedTotalUnits = 0;
	let paceValue = 0;

	if (units.totalMonthUnits) {
		const averageUnits = units.totalUnits / workedDays;
		projectedTotalUnits = averageUnits * daysInMonth;
		projectedTotalUnits = Number(projectedTotalUnits);
		paceValue = parseInt(String(projectedTotalUnits), 10);
	}

	return {
		paceValue,
	};
};

const getSalesUnits = (list: ISalesLog[] = [], day = 1, totalMonthDays = 1) => {
	return list.reduce(
		(result, sale) => {
			const date = moment(sale.saleDate).utc().format(DATE_FORMAT.dayPadded);
			const units = sale?.units ? Number(sale.units) : 0;

			// Total units of the current day
			if (day && Number(date) === Number(day)) {
				result.todayUnits += units;
			}

			// Total units till current day
			if (Number(date) <= Number(day)) {
				result.totalUnits += units;
			}

			// Total month units
			if (Number(date) <= Number(totalMonthDays)) {
				result.totalMonthUnits += units;
			}
			return result;
		},
		{
			todayUnits: 0,
			totalUnits: 0,
			totalMonthUnits: 0,
		}
	);
};

const getTradesUnits = (list: ISalesLog[] = [], day = 1, totalMonthDays = 1) => {
	return list.reduce(
		(result, sale) => {
			if (sale.tradeACV && sale.tradeACV !== 0) {
				const date = moment(sale.saleDate).utc().format(DATE_FORMAT.dayPadded);
				const units = sale?.units ? Number(sale.units) : 0;

				// Total units of the current day
				if (day && Number(date) === Number(day)) {
					result.todayUnits += units;
				}

				// Total units till current day
				if (Number(date) <= Number(day)) {
					result.totalUnits += units;
				}

				// Total month units
				if (Number(date) <= Number(totalMonthDays)) {
					result.totalMonthUnits += units;
				}
			}
			return result;
		},
		{
			todayUnits: 0,
			totalUnits: 0,
			totalMonthUnits: 0,
		}
	);
};

const getTotalDaily = (type: keyof ISalesLog, sales: ISalesLog[] = [], day = 0) => {
	let total = 0;
	if (sales?.length && type) {
		sales.forEach((element) => {
			const date = moment(element.saleDate).utc().format(DATE_FORMAT.dayPadded);
			if (Number(date) === Number(day)) {
				const sum = element[type];
				if (sum) {
					total += Number(sum);
				}
			}
		});
	}

	return total;
};

const getTradeAllowProfitValues = (frontProducts: ISalesProduct[]) => {
	let totalProfit = 0;
	frontProducts.forEach((product) => {
		if (product.product === "Trade Allow Profit") {
			totalProfit += product.value;
		}
	});

	return totalProfit;
};

const getTotalDailyHeldGross = (sales: ISalesLog[] = [], day = 0) => {
	let total = 0;
	if (sales?.length) {
		sales.forEach((sale) => {
			const date = moment(sale.saleDate).utc().format(DATE_FORMAT.dayPadded);
			if (Number(date) === Number(day) && sale.tradeACV && sale.tradeACV !== 0) {
				const tradeAllowProfit = getTradeAllowProfitValues(sale?.frontProducts);
				const add = Number(tradeAllowProfit) || 0;
				total += Number(add);
			}
		});
	}

	return total;
};

const calculatePVR = (amount: number, units: number) => {
	let pvr = 0;
	if (units && Number(units)) {
		const calculation = amount / units;
		pvr = Number(calculation);
	}
	return pvr;
};

const getTradesStatistics = (sales: ISalesLog[] = []) => {
	const result = {
		total: 0,
		held: 0,
	};

	const trades: Partial<ISalesLog & { tradeAllowProfit: number }>[] = [];
	if (sales.length) {
		sales.forEach((sale) => {
			if (sale.tradeACV && sale.tradeACV !== 0) {
				trades.push({
					units: sale.units,
					tradeACV: sale.tradeACV,
					tradeAllowProfit: getTradeAllowProfitValues(sale?.frontProducts),
				});
			}
		});
	}

	if (trades.length) {
		trades.forEach((trade) => {
			if (trade.tradeACV) {
				result.total += Number(trade.units);
				const sum = Number(result.held) + Number(trade.tradeAllowProfit);
				result.held = Math.round(sum * 100) / 100;
			}
		});
	}

	return result;
};

const getTotalByType = (type: keyof ISalesLog, sales: ISalesLog[] = [], day = 0) => {
	if (!sales?.length || !day || !type) {
		return 0;
	}
	return sales.reduce((total, sale) => {
		const date = moment(sale.saleDate).utc().format(DATE_FORMAT.dayPadded);
		if (Number(date) <= Number(day)) {
			const add = sale?.[type] || 0;
			total += Number(add);
		}
		return total;
	}, 0);
};

const getProjectedValue = (currentDay: number, totalDays: number, gross = 0) => {
	if (!currentDay) {
		return 0;
	}
	const projection = (gross / currentDay) * totalDays;
	return projection ? Number(projection) : 0;
};

const getTotalHeldGross = (sales: ISalesLog[] = [], day = 0) => {
	if (!sales?.length || !day) {
		return 0;
	}
	return sales.reduce((total, sale) => {
		const date = moment(sale.saleDate).utc().format(DATE_FORMAT.dayPadded);
		if (Number(date) <= Number(day) && sale.tradeACV && sale.tradeACV !== 0) {
			const tradeAllowProfit = getTradeAllowProfitValues(sale?.frontProducts);
			const add = Number(tradeAllowProfit) || 0;
			total += Number(add);
		}
		return total;
	}, 0);
};

const calculateAnalytics = (totalMonthDays: number, daysWorked = 1, sales: ISalesLog[] = []) => {
	const result = {
		summary: {
			deliveredToday: 0,
			totalDelivered: 0,
			totalTrades: 0,
			salesPace: 0,
			tradePace: 0,
			daysWorked: Number(daysWorked),
		},
		daily: {
			frontGross: 0,
			frontPVR: 0,
			backGross: 0,
			backPVR: 0,
			totalGross: 0,
			totalPVR: 0,
			heldGross: 0,
			heldPVR: 0,
		},
		projection: {
			current: {
				frontGross: 0,
				frontPVR: 0,
				backGross: 0,
				backPVR: 0,
				totalGross: 0,
				totalPVR: 0,
				heldGross: 0,
				heldPVR: 0,
			},
			projected: {
				frontGross: 0,
				frontPVR: 0,
				backGross: 0,
				backPVR: 0,
				totalGross: 0,
				totalPVR: 0,
				tradeGrossPace: 0,
				heldPVR: 0,
			},
		},
		trades: {
			total: 0,
			held: 0,
			perTrade: 0,
			pace: 0,
		},
	};

	if (sales?.length) {
		// Calculate units
		const units = getSalesUnits(sales, daysWorked, totalMonthDays);
		type TGetSalesUnits = ReturnType<typeof getTradesUnits>;
		const tradeUnits: TGetSalesUnits = getTradesUnits(sales, daysWorked, totalMonthDays);

		// Summary
		result.summary.deliveredToday = Number(units.todayUnits);
		result.summary.totalDelivered = Number(units.totalUnits);
		result.summary.totalTrades = Number(tradeUnits.totalUnits);

		if (units.totalMonthUnits) {
			const calculatedUnits = calculateProjectedUnits(units, daysWorked, totalMonthDays);
			result.summary.salesPace = calculatedUnits.paceValue;
		}

		if (tradeUnits.totalMonthUnits) {
			const calculatedTradeUnits = calculateProjectedUnits(tradeUnits, daysWorked, totalMonthDays);
			result.summary.tradePace = calculatedTradeUnits.paceValue;
		}

		// Daily calculations
		const dailyFrontGross = getTotalDaily("frontGrossProfit", sales, daysWorked);
		const dailyBackGross = getTotalDaily("backGrossProfit", sales, daysWorked);
		const dailyTotalGross = dailyFrontGross + dailyBackGross;
		const dailyHeldGross = getTotalDailyHeldGross(sales, daysWorked);

		result.daily.heldGross = Number(dailyHeldGross);
		result.daily.frontGross = Number(dailyFrontGross);
		result.daily.backGross = Number(dailyBackGross);
		result.daily.totalGross = Number(dailyTotalGross);

		if (units.todayUnits) {
			result.daily.frontPVR = calculatePVR(dailyFrontGross, units.todayUnits);
			result.daily.backPVR = calculatePVR(dailyBackGross, units.todayUnits);
			result.daily.totalPVR = calculatePVR(dailyTotalGross, units.todayUnits);
		}

		if (tradeUnits.todayUnits) {
			result.daily.heldPVR = calculatePVR(dailyHeldGross, tradeUnits.todayUnits);
		}

		// Projection
		const frontGross = getTotalByType("frontGrossProfit", sales, daysWorked);
		const backGross = getTotalByType("backGrossProfit", sales, daysWorked);
		const totalGross = frontGross + backGross;
		const heldGross = getTotalHeldGross(sales, daysWorked);

		result.projection.current.heldGross = heldGross;
		result.projection.current.frontGross = frontGross;
		result.projection.current.backGross = backGross;
		result.projection.current.totalGross = totalGross;

		const projectedHeldGross = getProjectedValue(daysWorked, totalMonthDays, heldGross);
		const projectedFrontGross = getProjectedValue(daysWorked, totalMonthDays, frontGross);
		const projectedBackGross = getProjectedValue(daysWorked, totalMonthDays, backGross);
		const projectedTotalGross = getProjectedValue(daysWorked, totalMonthDays, totalGross);
		result.projection.projected.frontGross = projectedFrontGross;
		result.projection.projected.backGross = projectedBackGross;
		result.projection.projected.totalGross = projectedTotalGross;
		result.projection.projected.tradeGrossPace = projectedHeldGross;

		if (units.totalMonthUnits) {
			result.projection.current.frontPVR = calculatePVR(frontGross, units.totalMonthUnits);
			result.projection.current.backPVR = calculatePVR(backGross, units.totalMonthUnits);
			result.projection.current.totalPVR = calculatePVR(totalGross, units.totalMonthUnits);

			const salesPace = result.summary.salesPace;
			result.projection.projected.frontPVR = calculatePVR(projectedFrontGross, salesPace);
			result.projection.projected.backPVR = calculatePVR(projectedBackGross, salesPace);
			result.projection.projected.totalPVR = calculatePVR(projectedTotalGross, salesPace);
		}

		if (tradeUnits.totalMonthUnits) {
			const tradePace = result.summary.tradePace;
			const tradesMTD = result.summary.totalTrades;
			result.projection.current.heldPVR = calculatePVR(heldGross, tradesMTD);
			result.projection.projected.heldPVR = calculatePVR(projectedHeldGross, tradePace);
		}

		// Trades
		const trades = getTradesStatistics(sales);
		result.trades.total = tradeUnits.todayUnits || 0;
		result.trades.held = trades.held || 0;
		result.trades.pace = getProjectedValue(daysWorked, totalMonthDays, result.trades.held);
		if (trades.total && Number(trades.total)) {
			const perTrade = trades.held / trades.total;
			result.trades.perTrade = Number(perTrade);
		}
	}
	return result;
};

export const getAnalytics = (date: Date | Moment, sales: ISalesLog[] = []): ISalesAnalyticsItem[] => {
	const totalMonthDays = moment(date).utc().daysInMonth();

	const finalSales = [];

	if (sales?.length) {
		const totalCurrentDays = moment.utc(date).daysInMonth();

		const cleanedSales = sales.filter((sale) => sale.dealStatus !== "BACKOUT");

		for (let currentDay = 1; currentDay <= totalMonthDays; currentDay++) {
			// For each day of the month
			const franchiseAnalytics = calculateAnalytics(totalCurrentDays, currentDay, cleanedSales);
			finalSales.push(franchiseAnalytics);
		}
	}

	const basicDay = {
		summary: {
			deliveredToday: 0,
			totalDelivered: 0,
			salesPace: 0,
			daysWorked: 0,
			totalTrades: 0,
			tradePace: 0,
		},
		daily: {
			frontGross: 0,
			frontPVR: 0,
			backGross: 0,
			backPVR: 0,
			totalGross: 0,
			totalPVR: 0,
			heldPVR: 0,
		},
		projection: {
			current: {
				frontGross: 0,
				frontPVR: 0,
				backGross: 0,
				backPVR: 0,
				totalGross: 0,
				totalPVR: 0,
				heldGross: 0,
				heldPVR: 0,
			},
			projected: {
				frontGross: 0,
				frontPVR: 0,
				backGross: 0,
				backPVR: 0,
				totalGross: 0,
				totalPVR: 0,
				tradeGrossPace: 0,
				heldPVR: 0,
			},
		},
		trades: {
			total: 0,
			held: 0,
			perTrade: 0,
			pace: 0,
		},
	};
	return finalSales.length ? finalSales : Array.from({ length: totalMonthDays }, (_, i) => ({ ...basicDay, day: i + 1 }));
};
